import axios from 'axios';
import SessionService from './SessionService';
import { config } from '../config';

const API_URL = config.API_BASE_URL;

const authHeaders = () => ({
  Accept: 'application/json',
  Authorization: `Bearer ${SessionService.getToken()}`,
});

export interface CrmAutomationRule {
  id: number;
  sectorId: number;
  columnId: number;
  title: string;
  instruction: string;
  triggerType: string;
  markerToken: string;
  enabled: boolean;
  sortOrder: number;
  aiAgentId?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CrmAutomationRuleWritePayload {
  columnId: number;
  title: string;
  instruction: string;
  enabled: boolean;
  aiAgentId: number;
}

export async function listCrmAutomationRules(sectorId: number): Promise<CrmAutomationRule[]> {
  const { data } = await axios.get<CrmAutomationRule[]>(`${API_URL}/crm-automation-rules`, {
    params: { sectorId },
    headers: authHeaders(),
  });
  return data;
}

export async function createCrmAutomationRule(
  sectorId: number,
  payload: CrmAutomationRuleWritePayload
): Promise<CrmAutomationRule> {
  const { data } = await axios.post<CrmAutomationRule>(`${API_URL}/crm-automation-rules`, payload, {
    params: { sectorId },
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
  });
  return data;
}

export async function updateCrmAutomationRule(
  sectorId: number,
  ruleId: number,
  payload: CrmAutomationRuleWritePayload
): Promise<void> {
  await axios.put(`${API_URL}/crm-automation-rules/${ruleId}`, payload, {
    params: { sectorId },
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
  });
}

export async function deleteCrmAutomationRule(sectorId: number, ruleId: number): Promise<void> {
  await axios.delete(`${API_URL}/crm-automation-rules/${ruleId}`, {
    params: { sectorId },
    headers: authHeaders(),
  });
}

export type CrmColumnKind = 'entrada' | 'etapa' | 'ganho' | 'perdido';

/** Condição em linguagem natural de uma coluna do Kanban (quando um contato pertence a ela). */
export interface CrmColumnCondition {
  columnId: number;
  columnName: string;
  position: number;
  kind: CrmColumnKind | null;
  ruleId: number | null;
  condition: string;
  enabled: boolean;
  /** false + condição preenchida = regra antiga que hoje só registra sugestões (modo sombra). */
  live: boolean;
  extraRules: number;
}

export interface CrmColumnConditionWritePayload {
  condition: string;
  kind: CrmColumnKind | null;
  enabled: boolean;
}

export type CrmFunnelTemplate = 'vendas' | 'simples';

export async function listColumnConditions(sectorId: number): Promise<CrmColumnCondition[]> {
  const { data } = await axios.get<CrmColumnCondition[]>(`${API_URL}/crm-automation-rules/conditions`, {
    params: { sectorId },
    headers: authHeaders(),
  });
  return Array.isArray(data) ? data : [];
}

export async function saveColumnCondition(
  sectorId: number,
  columnId: number,
  payload: CrmColumnConditionWritePayload
): Promise<CrmColumnCondition> {
  const { data } = await axios.put<CrmColumnCondition>(
    `${API_URL}/crm-automation-rules/conditions/${columnId}`,
    payload,
    {
      params: { sectorId },
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    }
  );
  return data;
}

/** Cria colunas que faltam e preenche condições vazias; nunca sobrescreve condição escrita nem move cards. */
export async function applyFunnelTemplate(
  sectorId: number,
  template: CrmFunnelTemplate
): Promise<CrmColumnCondition[]> {
  const { data } = await axios.post<CrmColumnCondition[]>(
    `${API_URL}/crm-automation-rules/template`,
    { template },
    {
      params: { sectorId },
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    }
  );
  return Array.isArray(data) ? data : [];
}

/** Pede ao worker para reanalisar as conversas dos últimos 30 dias (em segundo plano, aos poucos). */
export async function reevaluateFunnel(sectorId: number): Promise<{ conditions: number }> {
  const { data } = await axios.post<{ conditions: number }>(
    `${API_URL}/crm-automation-rules/reevaluate`,
    null,
    {
      params: { sectorId },
      headers: authHeaders(),
    }
  );
  return { conditions: Number(data?.conditions ?? 0) };
}

/**
 * Registro de uma avaliação/movimentação de card.
 * triggerType: on_conversation (IA pela conversa) | on_ai_reply (regra antiga) | manual (arraste) | registrar.
 */
export interface CrmAutomationLogEntry {
  id: number;
  contactId?: number;
  ruleId: number | null;
  ruleTitle: string | null;
  columnId: number | null;
  columnName: string | null;
  fromColumnId?: number | null;
  fromColumnName?: string | null;
  triggerType: string;
  outcome: string;
  /** Motivo. */
  detail: string | null;
  /** Trecho literal da conversa usado como evidência. */
  evidence?: string | null;
  confidence?: number | string | null;
  actorUserId?: number | null;
  actorName?: string | null;
  /** Oportunidade (card) movida; ausente nos registros antigos. */
  cardId?: number | null;
  createdAt: string;
}

export type CrmMoveLog = CrmAutomationLogEntry;

export async function listCrmAutomationLogs(
  sectorId: number,
  contactId: number
): Promise<CrmAutomationLogEntry[]> {
  const { data } = await axios.get<CrmAutomationLogEntry[]>(`${API_URL}/crm-automation-logs`, {
    params: { sectorId, contactId },
    headers: authHeaders(),
  });
  return data;
}

const LATEST_MOVES_CHUNK = 150;

/** Última movimentação APLICADA de cada contato (para o selo no card do Kanban). */
export async function listLatestMoves(sectorId: number, contactIds: number[]): Promise<CrmMoveLog[]> {
  const ids = Array.from(new Set(contactIds.filter((id) => Number.isFinite(id) && id > 0)));
  if (ids.length === 0) return [];
  // Lotes para a query string não estourar em setores grandes.
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += LATEST_MOVES_CHUNK) chunks.push(ids.slice(i, i + LATEST_MOVES_CHUNK));
  const pages = await Promise.all(
    chunks.map(async (chunk) => {
      const { data } = await axios.get<CrmMoveLog[]>(`${API_URL}/crm-automation-logs/latest`, {
        params: { sectorId, contactIds: chunk.join(',') },
        headers: authHeaders(),
      });
      return Array.isArray(data) ? data : [];
    })
  );
  return pages.flat();
}
